import { signInWithEmailAndPassword, signOut, createUserWithEmailAndPassword } from "firebase/auth";
import { collection, getDocs, query, where, doc, setDoc, addDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions"; 
import { auth, db } from "../firebaseConfig";

// 1. FONCTION DE CONNEXION (Support Multi-Admins)
export const loginAdmin = async (email: string, password: string, schoolId: string) => {
    const cleanEmail = email.trim().toLowerCase();
    const cleanSchoolId = schoolId.trim().toUpperCase();

    // Recherche du document de l'école via son ID personnalisé (ex: ECOL362)
    const schoolsRef = collection(db, "schools");
    const q = query(schoolsRef, where("schoolId", "==", cleanSchoolId));
    const schoolSnapshot = await getDocs(q);
    
    if (schoolSnapshot.empty) {
        throw new Error(`ID École '${cleanSchoolId}' incorrect.`);
    }

    const schoolDoc = schoolSnapshot.docs[0];
    const schoolData = schoolDoc.data();
    const firestoreId = schoolDoc.id;

    try {
        // Authentification Firebase Auth
        const userCredential = await signInWithEmailAndPassword(auth, cleanEmail, password);
        const user = userCredential.user;

        // VERIFICATION DE L'AUTORISATION :
        // 1. Est-ce le Super Admin principal ?
        const isMainAdmin = schoolData.adminEmail && schoolData.adminEmail.toLowerCase() === cleanEmail;

        if (!isMainAdmin) {
            // 2. Si ce n'est pas le Super Admin, vérifier s'il existe dans la sous-collection "admins" avec son UID exact
            const subAdminRef = doc(db, "schools", firestoreId, "admins", user.uid);
            const subAdminSnap = await getDoc(subAdminRef);

            if (!subAdminSnap.exists()) {
                // Déconnexion immédiate si le compte Auth existe mais n'est pas rattaché à cette école
                await signOut(auth);
                throw new Error("Cet email n'est pas autorisé pour cette école.");
            }
        }

        return {
            uid: user.uid,
            firestoreId: firestoreId, 
            schoolCustomId: cleanSchoolId,
            schoolName: schoolData.schoolName,
            mustChangePassword: schoolData.mustChangePassword || false
        };
    } catch (error: any) {
        if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
            throw new Error("Mot de passe incorrect.");
        }
        throw error;
    }
};

// 2. FONCTION D'ENREGISTREMENT
export const registerNewSchool = async (schoolName: string, adminEmail: string, address: string, phone: string) => {
    const cleanEmail = adminEmail.trim().toLowerCase();
    
    const baseId = schoolName
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "") 
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .substring(0, 4);
    
    const randomDigits = Math.floor(100 + Math.random() * 900);
    const customSchoolId = `${baseId}${randomDigits}`;

    const q = query(collection(db, "schools"), where("schoolId", "==", customSchoolId));
    const checkId = await getDocs(q);
    if (!checkId.empty) {
        throw new Error(`L'ID ${customSchoolId} est déjà utilisé. Réessayez.`);
    }

    const tempPassword = Math.random().toString(36).slice(-10) + "!";

    try {
        // 1. Création du compte dans Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, tempPassword);
        const userUid = userCredential.user.uid;
        
        // 👇 LE FIX EST ICI : On force la connexion immédiate pour que Firestore autorise l'écriture
        await signInWithEmailAndPassword(auth, cleanEmail, tempPassword);
        
        // 2. Création du document principal de l'école
        const schoolRef = await addDoc(collection(db, "schools"), {
            schoolId: customSchoolId,
            schoolName: schoolName,
            adminEmail: cleanEmail,
            schoolAddress: address,
            schoolPhone: phone,
            createdAt: serverTimestamp(),
            mustChangePassword: true,
            status: 'Actif',
            latitude: 33.5850,
            longitude: -7.6327
        });

        // 3. Enregistrer aussi le Super Admin dans sa propre sous-collection "admins" en utilisant son UID exact
        const adminDocRef = doc(db, "schools", schoolRef.id, "admins", userUid);
        await setDoc(adminDocRef, {
            email: cleanEmail,
            name: "Super Admin",
            role: "super_admin",
            createdAt: serverTimestamp()
        });

        // 👇 LE FIX EST ICI : On utilise la Cloud Function avec la bonne région
        try {
            const functions = getFunctions(auth.app, 'us-central1'); // ✅ Correction appliquée
            const sendEmailFn = httpsCallable(functions, 'sendEmail');
            
            await sendEmailFn({
                to: cleanEmail,
                subject: `Bienvenue - Vos accès ${schoolName}`,
                html: `
                <div style="font-family: Arial; border: 1px solid #eee; padding: 20px;">
                    <h3>Bienvenue sur GPSchoolBus</h3>
                    <p>Voici vos identifiants pour l'école <b>${schoolName}</b> :</p>
                    <p><b>ID École :</b> <code style="background:#eee; padding:5px;">${customSchoolId}</code></p>
                    <p><b>Email :</b> ${cleanEmail}</p>
                    <p><b>Mot de passe temporaire :</b> <span style="color:red;">${tempPassword}</span></p>
                    <br/>
                    <p><i>Note : Vous devrez changer ce mot de passe à la première connexion.</i></p>
                </div>`,
                senderType: 'server' 
            });
            console.log("✅ Email envoyé avec succès");
        } catch (emailError) {
            console.error("Erreur d'envoi d'email (non bloquant):", emailError);
        }

        return { customSchoolId, tempPassword };
    } catch (error: any) {
        throw new Error(error.message);
    }
};